import { endpoints, headerConfigs } from "../../utils/config";

/**
 * The write requests, over `fetch` the way the rest of this theme talks to
 * Shopify. A non-2xx answer throws, which is what makes an optimistic change
 * roll back instead of sitting on screen as something the cart never accepted.
 */
const postCart = async (url, body) => {
  const response = await fetch(url, {
    method: "POST",
    headers: headerConfigs.headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`${url} answered ${response.status}`);
  }
  return response.json();
};

/**
 * The cart applies every change on screen straight away and sends the requests
 * behind it. Before this, each action opened with `if (state.loading) return`
 * and awaited two round trips — the write and the `/cart.js` after it — so the
 * second tap on a stepper was dropped and the row sat dimmed in between.
 *
 * What that costs, and how it is paid for:
 *
 *   - Requests must still reach Shopify one at a time, or two updates to the
 *     same line race and the loser wins. `queueCartMutation` chains them.
 *   - `/cart/add.js` gives a line a key only once it has been created, and so
 *     does a selling-plan change. Until then the line is on screen under an
 *     optimistic key, so `keyAliases` maps that to the real one and queued
 *     requests resolve it when they run — the buyer can keep pressing + on a
 *     line whose add is still in flight.
 *   - Totals are recomputed locally. Cart-level discounts are not in the line
 *     prices, so the last server totals are kept and applied on top; otherwise
 *     a discount would vanish until the server answered.
 *
 * An optimistic line has to carry every field the row reads, or the row shows a
 * gap until the server answers. What `CartItem`, `StoreCart` and `CartPage`
 * read today: key, id, variant_id, product_id, title, variant_title, handle,
 * url, sku, available, quantity, price, final_price, compare_at_price,
 * line_price, final_line_price, original_line_price, total_discount, image,
 * featured_image, properties (`details`, `_bars`, `_subscribePriceDay`) and
 * selling_plan_allocation with selling_plan.id/.name.
 */

const OPTIMISTIC_KEY_PREFIX = "__opt_";

let mutationChain = Promise.resolve();
let pendingMutations = 0;
// Bumped by every queued mutation, so a background refresh can tell that the
// cart changed while it was in flight.
let cartVersion = 0;
// Only the latest background refresh may report. One skipped because changes
// were still queued is dropped: whichever action drains the queue asks for its
// own refresh afterwards.
let refreshSeq = 0;

const keyAliases = new Map();

const resolveKey = (key) => {
  let current = key;
  const seen = new Set();
  while (keyAliases.has(current) && !seen.has(current)) {
    seen.add(current);
    current = keyAliases.get(current);
  }
  return current;
};

const isOptimisticKey = (key) =>
  typeof key === "string" && key.startsWith(OPTIMISTIC_KEY_PREFIX);

const buildOptimisticKey = (variantId) =>
  `${OPTIMISTIC_KEY_PREFIX}${variantId}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const findLine = (state, key) => {
  const items = state.cartData?.items || [];
  return (
    items.find((i) => i.key === key) ||
    items.find((i) => i.key === resolveKey(key))
  );
};

const planIdOf = (line) =>
  line?.selling_plan_allocation?.selling_plan?.id ?? null;

/**
 * The line an add would land on, or null for a new one. Shopify merges an add
 * into an existing line when the variant, the selling plan and the line
 * properties all match — so the preview has to do the same, or the product
 * shows twice until the refresh merges them.
 */
const findMergeTarget = (state, payload) => {
  const variantId = Number(payload.id);
  const planId = payload.selling_plan ?? null;
  const properties = JSON.stringify(payload.properties || {});

  return (state.cartData?.items || []).find(
    (line) =>
      Number(line.variant_id ?? line.id) === variantId &&
      String(planIdOf(line) ?? "") === String(planId ?? "") &&
      JSON.stringify(line.properties || {}) === properties
  );
};

const queueCartMutation = (commit, task) => {
  cartVersion++;
  pendingMutations++;
  if (pendingMutations === 1) commit("cartLoading", true);

  const result = mutationChain.then(task, task);
  mutationChain = result.catch(() => {});

  return result.finally(() => {
    pendingMutations--;
    if (pendingMutations === 0) commit("cartLoading", false);
  });
};

// How the last cart Shopify sent got from its lines to its total.
let serverTotals = null;

const snapshotTotals = (cart) => {
  const linesTotal = (cart?.items || []).reduce(
    (sum, i) => sum + (Number(i.final_line_price) || 0),
    0
  );
  const discounts = (cart?.cart_level_discount_applications || []).map((d) => ({
    percentage: d.value_type === "percentage",
    amount: Number(d.total_allocated_amount) || 0,
  }));
  const discount = discounts.reduce((sum, d) => sum + d.amount, 0);
  return {
    linesTotal,
    discounts,
    residual: (Number(cart?.total_price) || 0) - (linesTotal - discount),
  };
};

/**
 * The header's count is a plain element the Liquid renders and the theme's
 * mini-cart rewrites after its own refresh, so it has to be kept in step here
 * too — otherwise the drawer showed the new line while the header still said
 * what the page was served with.
 */
const syncMiniCount = (count) => {
  const el = document.querySelector("[data-cart-mini-count]");
  if (el) el.textContent = count === 0 ? "" : count;
};

const recomputeTotals = (cart) => {
  if (!cart || !Array.isArray(cart.items)) return;

  let count = 0;
  let subtotal = 0;
  let total = 0;
  for (const item of cart.items) {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const finalPrice = Number(item.final_price ?? price);
    count += qty;
    subtotal += price * qty;
    total += finalPrice * qty;
    item.line_price = price * qty;
    item.original_line_price = price * qty;
    item.final_line_price = finalPrice * qty;
  }

  if (serverTotals && cart.items.length) {
    const ratio =
      serverTotals.linesTotal > 0 ? total / serverTotals.linesTotal : 1;
    const discount = serverTotals.discounts.reduce(
      (sum, d) => sum + (d.percentage ? d.amount * ratio : d.amount),
      0
    );
    total = Math.max(
      0,
      Math.round(total - Math.min(discount, total) + serverTotals.residual)
    );
  }

  cart.item_count = count;
  cart.items_subtotal_price = subtotal;
  cart.total_price = total;

  syncMiniCount(count);
};

/**
 * The `selling_plan_allocation` Shopify would send for `planId`. The plan
 * object from the product JSON is used whole, because a row may read anything
 * on it.
 */
const buildAllocation = (product, planId, price, compareAtPrice, planName) => {
  const plan = product?.selling_plan_groups
    ?.flatMap((group) => group.selling_plans)
    .find((p) => p.id == planId);

  return {
    // The row prints this name above the line ("14 bars for 1-week trial,
    // then 42 bars every 3 weeks"), so an add that knows its plan sends the
    // name with it instead of leaving the heading blank until the refresh.
    selling_plan: plan || {
      id: planId,
      name: planName || "",
      price_adjustments: [],
    },
    price,
    compare_at_price: compareAtPrice,
    per_delivery_price: price,
    price_adjustments: [],
  };
};

/**
 * The line to show for an add that has not reached Shopify yet, from the
 * product JSON already loaded for the cart or from an `_optimistic` override on
 * the payload for a caller that knows the product without it being cached —
 * which is every add of something not already in the cart.
 *
 * Returns null when neither is available: a line with no title and no price is
 * worse than the row appearing a moment later. The rest of the add stays
 * non-blocking either way.
 */
const buildOptimisticLine = (payload, productsCache) => {
  const variantId = Number(payload.id);
  const quantity = Number(payload.quantity) || 1;
  const override = payload._optimistic || {};

  const cachedProduct = productsCache?.[variantId];
  const variant = cachedProduct?.variants?.find((v) => v.id == variantId);

  const productTitle = override.product_title || cachedProduct?.title || "";
  const variantTitleRaw =
    override.variant_title || variant?.title || variant?.public_title || "";
  const variantTitle =
    variantTitleRaw === "Default Title" ? "" : variantTitleRaw;
  const title =
    override.title ||
    (variantTitle ? `${productTitle} - ${variantTitle}` : productTitle);
  const handle = override.handle || cachedProduct?.handle || "";
  // `nil | img_url` in Liquid hands back the admin's "no image" placeholder,
  // which is a non-empty string — so a caller passing a variant image has to
  // have checked the variant actually has one.
  const image =
    override.image ||
    variant?.featured_image?.src ||
    variant?.featured_image ||
    cachedProduct?.featured_image?.src ||
    cachedProduct?.featured_image ||
    cachedProduct?.images?.[0] ||
    "";

  const price = Number(
    override.price ?? variant?.price ?? cachedProduct?.price ?? NaN
  );
  const compareAt =
    Number(
      override.compare_at_price ??
        variant?.compare_at_price ??
        variant?.compareAtPrice ??
        cachedProduct?.compare_at_price ??
        cachedProduct?.compareAtPrice
    ) || null;

  if (!title || !Number.isFinite(price)) return null;

  const line = {
    key: buildOptimisticKey(variantId),
    id: variantId,
    variant_id: variantId,
    // The row matches this with `===` against ids coming from Liquid, so a
    // serializer that quotes the id would never match unless it is coerced.
    product_id: Number(override.product_id ?? cachedProduct?.id) || null,
    handle,
    url:
      override.url ||
      (handle
        ? `/products/${handle}${variantId ? `?variant=${variantId}` : ""}`
        : "#"),
    title,
    product_title: productTitle || title,
    variant_title: variantTitle,
    sku: override.sku || variant?.sku || cachedProduct?.sku || "",
    // The row hides the stepper on an unavailable line, so an add that omitted
    // this read as sold out for a moment.
    available: override.available ?? variant?.available ?? true,
    quantity,
    price,
    final_price: price,
    original_price: price,
    // The drawer's savings figure reads this and treats a missing one as zero,
    // which made every line look like its whole compare-at price was saved
    // until the server answered.
    discounted_price: price,
    compare_at_price: compareAt,
    line_price: price * quantity,
    final_line_price: price * quantity,
    original_line_price: (compareAt || price) * quantity,
    total_discount: 0,
    discounts: [],
    line_level_discount_allocations: [],
    image,
    // The row falls through to `item.images[0]?.src` when `image` is empty, and
    // reads it without guarding the array itself — a row that throws while
    // rendering takes the whole cart off the screen.
    images: [],
    featured_image: { url: image, src: image, alt: title },
    // Always an object: the row reads `item.properties.details` unguarded.
    properties: payload.properties || {},
    _optimistic: true,
  };

  // Shopify omits this key entirely on a one-time line, and a row that keys a
  // subscription block off `"selling_plan_allocation" in item` would take a key
  // holding `null` as a subscription.
  if (payload.selling_plan) {
    line.selling_plan_allocation = buildAllocation(
      cachedProduct,
      payload.selling_plan,
      price,
      compareAt || price,
      override.selling_plan_name
    );
  }

  return line;
};

// What a line costs on `planId` (null = one-time), so the price moves the
// moment the buyer switches. Falls back to the current price while the product
// JSON is still loading.
const optimisticPlan = (product, line, planId) => {
  const variant = product?.variants?.find((v) => v.id == line.id);
  const allocation = planId
    ? variant?.selling_plan_allocations?.find(
        (a) => a.selling_plan_id == planId
      )
    : null;
  const price = Number(
    (planId ? allocation?.price : variant?.price) ?? line.price
  );
  const discountShare = line.price > 0 ? line.final_price / line.price : 1;

  if (!planId) {
    return { allocation: null, price, finalPrice: price * discountShare };
  }

  return {
    allocation: buildAllocation(
      product,
      planId,
      price,
      Number(
        allocation?.compare_at_price ?? line.compare_at_price ?? line.price
      )
    ),
    price,
    finalPrice: price * discountShare,
  };
};

// Carries each line's row identity across a server refresh. A line that got a
// new key keeps the key it was rendered with, so the row is updated in place
// instead of being remounted.
const carryViewKeys = (prevItems, nextItems) => {
  const viewKeys = new Map();
  for (const item of prevItems || []) {
    viewKeys.set(resolveKey(item.key), item.viewKey || item.key);
  }

  const used = new Set();
  for (const item of nextItems || []) {
    const viewKey = viewKeys.get(item.key);
    item.viewKey = viewKey && !used.has(viewKey) ? viewKey : item.key;
    used.add(item.viewKey);
  }
};

const ensureCart = (state) => {
  if (!state.cartData) {
    state.cartData = {
      items: [],
      item_count: 0,
      total_price: 0,
      items_subtotal_price: 0,
      total_discount: 0,
    };
  }
  if (!Array.isArray(state.cartData.items)) state.cartData.items = [];
};

/**
 * state
 */
const state = {
  visible: false,
  cartData: false,
  loading: false,
  // Kept for the row, which reads it, though nothing blocks on it any more.
  loadingItems: {},
  products: [],
  productsWithDetails: [],
};

/**
 * getters
 */
const getters = {
  visible: () => state.visible,
  cartCount: () => state.cartData?.item_count || 0,
};

/**
 * mutations
 */
const mutations = {
  TOGGLE(state) {
    state.visible = !state.visible;
  },

  SHOW(state) {
    state.visible = true;
  },

  HIDE(state) {
    state.visible = false;
  },

  cartLoading(state, payload) {
    state.loading = typeof payload === "boolean" ? payload : !state.loading;
  },

  initCart(state, payload) {
    // An optimistic line whose add already went through is in the payload
    // under its real key; keeping it too would show the product twice.
    const optimisticItems =
      state.cartData?.items?.filter(
        (i) => i._optimistic && !keyAliases.has(i.key)
      ) || [];

    carryViewKeys(state.cartData?.items, payload?.items);
    if (payload) serverTotals = snapshotTotals(payload);

    if (pendingMutations > 0 && optimisticItems.length && payload) {
      const merged = {
        ...payload,
        items: [...optimisticItems, ...(payload.items || [])],
      };
      recomputeTotals(merged);
      state.cartData = merged;
      return;
    }

    state.cartData = payload;
    if (payload) syncMiniCount(payload.item_count);
  },

  pushOptimisticItem(state, item) {
    ensureCart(state);
    state.cartData.items.unshift(item);
    recomputeTotals(state.cartData);
  },

  removeItemByKey(state, key) {
    if (!state.cartData) return;
    const idx = state.cartData.items.indexOf(findLine(state, key));
    if (idx !== -1) {
      state.cartData.items.splice(idx, 1);
      recomputeTotals(state.cartData);
    }
  },

  // Used when a plan change moves the line to a different product: the row
  // keeps its place and its viewKey, so it is patched rather than torn down
  // and rebuilt in a different spot.
  replaceItem(state, { key, item }) {
    if (!state.cartData) return;
    const previous = findLine(state, key);
    const idx = state.cartData.items.indexOf(previous);
    if (idx === -1) return;
    state.cartData.items.splice(idx, 1, {
      ...item,
      viewKey: previous.viewKey || previous.key,
    });
    recomputeTotals(state.cartData);
  },

  restoreItem(state, { item, index }) {
    if (!state.cartData) return;
    const safeIndex = Math.max(0, Math.min(index, state.cartData.items.length));
    state.cartData.items.splice(safeIndex, 0, item);
    recomputeTotals(state.cartData);
  },

  setItemQuantity(state, { key, quantity }) {
    const item = findLine(state, key);
    if (!item) return;
    item.quantity = quantity;
    recomputeTotals(state.cartData);
  },

  setItemPlan(state, { key, allocation, price, finalPrice }) {
    const item = findLine(state, key);
    if (!item) return;
    if (allocation) {
      item.selling_plan_allocation = allocation;
    } else {
      delete item.selling_plan_allocation;
    }
    if (price != null) {
      item.price = price;
      item.final_price = finalPrice ?? price;
      item.discounted_price = finalPrice ?? price;
      recomputeTotals(state.cartData);
    }
  },

  setItemLoading(state, { key, value }) {
    state.loadingItems = { ...state.loadingItems, [key]: value };
  },

  setDiscountCode(state, payload) {
    state.cartData.cart_level_discount_applications.push(payload);
  },
  setProductDetails(state, { variantId, product }) {
    state.productsWithDetails[variantId] = product;
  },
};

/**
 * actions
 */
const actions = {
  toggle({ commit }) {
    commit("TOGGLE");
  },

  show({ commit }) {
    commit("SHOW");
  },

  hide({ commit }) {
    commit("HIDE");
  },
  setCode({ commit }, payload) {
    commit("setDiscountCode", payload);
  },

  // `background` is the refresh that follows the buyer's own changes. If the
  // cart changed again while it was in flight, its snapshot is already stale
  // and would briefly undo that change on screen, so it is dropped: the
  // refresh after the newer change reports the final state.
  async initCart({ commit, dispatch }, { background = false } = {}) {
    const version = cartVersion;
    const seq = background ? ++refreshSeq : refreshSeq;

    try {
      const response = await fetch(endpoints.cart, {
        method: "GET",
        headers: headerConfigs.headers,
      });
      const data = await response.json();

      if (background) {
        if (seq !== refreshSeq) return data;
        if (pendingMutations > 0) return data;
        // The change that made this stale has already drained: fetch again.
        if (version !== cartVersion) {
          return dispatch("initCart", { background: true });
        }
      }

      commit("initCart", data);

      if (data?.items?.length) {
        await Promise.allSettled(
          data.items.map((item) => dispatch("loadProductDetails", item))
        );
      }

      return data;
    } catch (error) {
      console.error("Init cart error:", error);
      throw error;
    }
  },

  removeItem: async ({ commit, state, dispatch }, payload) => {
    const products = Array.isArray(payload) ? payload : [payload];
    if (!products.length) return;

    const snapshots = products
      .map((item) => {
        const line = findLine(state, item.key);
        if (!line) return null;
        return { item: line, index: state.cartData.items.indexOf(line) };
      })
      .filter(Boolean);

    for (const snapshot of snapshots) {
      commit("removeItemByKey", snapshot.item.key);
    }

    try {
      await queueCartMutation(commit, () => {
        // Keys are resolved only now, so a line removed while its add was
        // still in flight is removed by its real key.
        const updates = {};
        for (const item of products) {
          const key = resolveKey(item.key);
          if (!isOptimisticKey(key)) updates[key] = 0;
        }
        if (!Object.keys(updates).length) return null;
        return postCart(endpoints.update, { updates });
      });
      if (pendingMutations === 0) dispatch("initCart", { background: true });
    } catch (error) {
      console.error("Remove item error:", error);
      for (const snapshot of [...snapshots].reverse()) {
        commit("restoreItem", snapshot);
      }
      dispatch("initCart", { background: true });
    }
  },

  addItem: async ({ commit, state, dispatch }, payload) => {
    let products = Array.isArray(payload) ? payload : [payload];

    // Without a variant id the request only ever 404s, so stop here instead of
    // leaving the cart in a loading state nothing can recover from.
    if (!products.length || products.some((item) => !item?.id)) {
      console.error("Add to cart called without a variant id:", products);
      return;
    }

    const optimisticLines = [];
    const mergedLines = [];

    for (const item of products) {
      const existing = findMergeTarget(state, item);

      // Shopify merges an add for a variant already in the cart into that
      // line, so the preview does the same — pushing a second row showed the
      // product twice until the refresh merged them.
      if (existing) {
        mergedLines.push({ key: existing.key, quantity: existing.quantity });
        commit("setItemQuantity", {
          key: existing.key,
          quantity: existing.quantity + (Number(item.quantity) || 1),
        });
        continue;
      }

      const line = buildOptimisticLine(item, state.productsWithDetails);
      if (!line) continue;

      optimisticLines.push(line);
      commit("pushOptimisticItem", line);

      // The row's strike price and its plan dropdown are both gated on the
      // product JSON, which is fetched per line and so arrived seconds after
      // the row itself. An add that knows either hands it over now; the fetch
      // still runs and replaces this with the full product.
      const groups = item._optimistic?.selling_plan_groups;
      if (
        (groups?.length || line.compare_at_price) &&
        !state.productsWithDetails[line.id]
      ) {
        commit("setProductDetails", {
          variantId: line.id,
          product: {
            id: line.product_id,
            title: line.product_title,
            handle: line.handle,
            // The row takes its strike price from here, not from the line.
            compare_at_price: line.compare_at_price,
            selling_plan_groups: groups,
            _provisional: true,
          },
        });
      }

      // No cache check here: the fetch itself skips a line whose product is
      // already loaded, and it has to run even when the seed above just filled
      // the entry with the handful of plans this add knew about.
      if (line.handle) dispatch("loadProductDetails", line);
    }

    // Hand over to the sliding cart now, not after the response: the line is
    // already on screen.
    dispatch("show");

    // Rebuilt rather than forwarded: `_optimistic` is for drawing the row and
    // must never reach Shopify.
    const apiItems = products.map((item) => {
      const out = { id: item.id, quantity: Number(item.quantity) || 1 };
      if (item.selling_plan) out.selling_plan = item.selling_plan;
      if (item.properties) out.properties = item.properties;
      return out;
    });

    try {
      await queueCartMutation(commit, async () => {
        const response = await postCart(endpoints.add, { items: apiItems });
        // Inside the task, so requests queued behind the add already see the
        // real keys when they run. Only paired up when every line was shown
        // optimistically — otherwise the indexes do not line up.
        if (optimisticLines.length === products.length) {
          (response?.items || []).forEach((real, i) => {
            if (real?.key && optimisticLines[i]) {
              keyAliases.set(optimisticLines[i].key, real.key);
            }
          });
        }
        return response;
      });

      // Always refresh when nothing was shown up front: that request is what
      // puts the line on screen.
      if (
        pendingMutations === 0 ||
        (!optimisticLines.length && !mergedLines.length)
      ) {
        dispatch("initCart", { background: true });
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      for (const line of optimisticLines) {
        commit("removeItemByKey", line.key);
      }
      for (const line of mergedLines) {
        commit("setItemQuantity", line);
      }
      dispatch("initCart", { background: true });
    }
  },

  updateItem: async ({ commit, dispatch }, payload) => {
    const { item, type } = payload;

    let quantity;
    if (type === "increment") quantity = item.quantity + 1;
    else if (type === "decrement") quantity = item.quantity - 1;
    else if (type === "update") quantity = Number(payload.qty);
    else quantity = item.quantity;

    if (!Number.isFinite(quantity) || quantity < 0) return;
    // Taking the line to 0 removes it: drop the row now rather than showing a
    // 0-quantity line until the server answers.
    if (quantity === 0) return dispatch("removeItem", item);

    const previousQuantity = item.quantity;
    commit("setItemQuantity", { key: item.key, quantity });

    try {
      await queueCartMutation(commit, () => {
        const key = resolveKey(item.key);
        // The add for this line failed: there is nothing to update.
        if (isOptimisticKey(key)) return null;
        return postCart(endpoints.update, { updates: { [key]: quantity } });
      });
      if (pendingMutations === 0) dispatch("initCart", { background: true });
    } catch (error) {
      console.error("Update item error:", error);
      commit("setItemQuantity", { key: item.key, quantity: previousQuantity });
      dispatch("initCart", { background: true });
    }
  },
  async updatePlan({ commit, dispatch, state }, { planId, item, row }) {
    const cartItem = findLine(state, item.key);
    if (!cartItem) return;

    // Another trial is another product: `change.js` can move a line onto a
    // different plan but not onto a different variant, so this one has to be
    // removed and the other one added — which is what the swap below does.
    const targetVariant = Number(item.variant_id || item.id);
    const currentVariant = Number(cartItem.variant_id || cartItem.id);
    if (targetVariant && targetVariant !== currentVariant) {
      return dispatch("swapVariant", { planId, item, cartItem, row });
    }

    const previous = {
      allocation: cartItem.selling_plan_allocation || null,
      price: cartItem.price,
      finalPrice: cartItem.final_price,
    };
    commit("setItemPlan", {
      key: item.key,
      ...optimisticPlan(
        state.productsWithDetails[cartItem.id],
        cartItem,
        planId
      ),
    });

    try {
      await queueCartMutation(commit, async () => {
        const key = resolveKey(item.key);
        // The add for this line failed: there is nothing to change.
        if (isOptimisticKey(key)) return;

        // `change.js` switches the plan on the line itself: one request, and
        // the line keeps its properties and its place in the cart. Removing it
        // and adding it back sent two, and moved it to the end.
        const cart = await postCart(endpoints.change, {
          id: key,
          quantity: findLine(state, item.key)?.quantity ?? item.quantity,
          selling_plan: planId || null,
        });

        // The line comes back under a new key; point the old one at it so
        // requests queued behind this one reach the right line.
        const otherKeys = new Set(
          (state.cartData?.items || [])
            .filter((i) => i !== findLine(state, item.key))
            .map((i) => resolveKey(i.key))
        );
        const matches = (cart?.items || []).filter(
          (i) =>
            i.variant_id == (cartItem.variant_id || cartItem.id) &&
            planIdOf(i) == (planId || null)
        );
        const changed =
          matches.find((i) => !otherKeys.has(i.key)) || matches[0];
        if (changed && changed.key !== key) keyAliases.set(key, changed.key);
      });

      if (pendingMutations === 0) dispatch("initCart", { background: true });
    } catch (error) {
      console.error("Update plan error:", error);
      commit("setItemPlan", { key: item.key, ...previous });
      dispatch("initCart", { background: true });
    }
  },
  /**
   * Moves a line onto another product's variant, keeping its quantity and
   * properties: the row changes on the spot and the two requests Shopify needs
   * for it go out behind that, one after the other like every other mutation.
   */
  async swapVariant(
    { commit, dispatch, state },
    { planId, item, cartItem, row }
  ) {
    const quantity = cartItem.quantity;
    const previous = { ...cartItem };
    const index = (state.cartData?.items || []).indexOf(cartItem);

    const line = buildOptimisticLine(
      {
        id: item.variant_id || item.id,
        quantity,
        selling_plan: planId,
        properties: cartItem.properties,
        _optimistic: row || {},
      },
      state.productsWithDetails
    );

    // Nothing worth drawing: fall back to doing it silently and letting the
    // refresh bring the line back, which is how this worked before.
    if (line) {
      commit("replaceItem", { key: item.key, item: line });

      // Same reason as in `addItem`: the row's strike price and plan dropdown
      // are gated on the product JSON, and this line's product has never been
      // fetched.
      const groups = row?.selling_plan_groups;
      if (
        (groups?.length || line.compare_at_price) &&
        !state.productsWithDetails[line.id]
      ) {
        commit("setProductDetails", {
          variantId: line.id,
          product: {
            id: line.product_id,
            title: line.product_title,
            handle: line.handle,
            // The row takes its strike price from here, not from the line.
            compare_at_price: line.compare_at_price,
            selling_plan_groups: groups,
            _provisional: true,
          },
        });
      }

      if (line.handle) dispatch("loadProductDetails", line);
    }

    try {
      await queueCartMutation(commit, async () => {
        const key = resolveKey(item.key);
        if (!isOptimisticKey(key)) {
          await postCart(endpoints.update, { updates: { [key]: 0 } });
        }

        const data = await postCart(endpoints.add, {
          items: [
            {
              id: item.variant_id || item.id,
              quantity,
              selling_plan: planId,
              ...(cartItem.properties
                ? { properties: cartItem.properties }
                : {}),
            },
          ],
        });

        const created = data?.items?.[0];
        if (line && created?.key) keyAliases.set(line.key, created.key);
      });

      if (pendingMutations === 0) dispatch("initCart", { background: true });
    } catch (error) {
      console.error("Swap variant error:", error);
      if (line) {
        commit("removeItemByKey", line.key);
        commit("restoreItem", { item: previous, index: Math.max(0, index) });
      }
      dispatch("initCart", { background: true });
    }
  },

  async loadProductDetails({ commit, state }, cartItem) {
    // A provisional entry holds only the plans an add handed over, so it is
    // replaced by the real product rather than standing in for it.
    const cached = state.productsWithDetails[cartItem.id];
    if (cached && !cached._provisional) return;

    // console.log("cart item ", cartItem);
    try {
      const res = await fetch(`/products/${cartItem.handle}.js`);
      // console.log("res: ", res);
      if (!res.ok) return;

      const product = await res.json();

      const variant = product.variants?.find((v) => v.id == cartItem.id);

      commit("setProductDetails", {
        variantId: cartItem.id,
        product,
        variant,
      });
      // console.log("product details: ", product);
    } catch (e) {
      console.error("Product details error", e);
    }
  },
};

/**
 * export
 */
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
